import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth } from '../../contexts/AuthContext';
import Modal from '../../components/ui/Modal';
import { useToast } from '../../components/ui/Toast';
import {
  createCurrencySchema,
  editCurrencySchema,
  type CreateCurrencyFormData,
  type EditCurrencyFormData,
} from '../../schemas/currency';

interface Entity {
  id: string;
  code: string;
  name: string;
}

interface Currency {
  id: string;
  code: string;
  name: string;
  symbol: string | null;
  type: 'fiat' | 'crypto';
  decimals: number;
  is_active: boolean;
  entities: Entity[];
}

export default function CurrenciesPage() {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const toast = useToast();
  const [showForm, setShowForm] = useState(false);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [collapsedSections, setCollapsedSections] = useState<Set<string>>(new Set());

  // Filters
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [entityFilter, setEntityFilter] = useState<string>('all');

  const isAdmin = user?.role === 'admin';

  const { data, isLoading } = useQuery({
    queryKey: ['currencies-all'],
    queryFn: async () => {
      const res = await fetch('/api/admin/currencies?includeInactive=true', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load currencies');
      return res.json();
    },
  });

  const { data: entitiesData } = useQuery({
    queryKey: ['entities'],
    queryFn: async () => {
      const res = await fetch('/api/admin/entities', { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load entities');
      return res.json();
    },
  });

  const entities: Entity[] = entitiesData?.data || [];

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...data }: { id: string; isActive?: boolean }) => {
      const res = await fetch(`/api/admin/currencies/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to update currency');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['currencies-all'] });
      queryClient.invalidateQueries({ queryKey: ['currencies'] });
    },
    onError: (err: Error) => toast.error(err.message),
  });

  const handleToggleActive = (currency: Currency) => {
    updateMutation.mutate({
      id: currency.id,
      isActive: !currency.is_active,
    });
  };

  const toggleSection = (section: string) => {
    setCollapsedSections(prev => {
      const next = new Set(prev);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  };

  const currencies: Currency[] = data?.data || [];

  // Filter currencies
  const filteredCurrencies = useMemo(() => {
    let filtered = [...currencies];

    // Search filter
    if (search) {
      const s = search.toLowerCase();
      filtered = filtered.filter(c =>
        c.code.toLowerCase().includes(s) ||
        c.name.toLowerCase().includes(s) ||
        (c.symbol && c.symbol.toLowerCase().includes(s))
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      const isActive = statusFilter === 'active';
      filtered = filtered.filter(c => c.is_active === isActive);
    }

    // Entity filter
    if (entityFilter !== 'all') {
      filtered = filtered.filter(c =>
        c.entities.some(e => e.id === entityFilter)
      );
    }

    return filtered;
  }, [currencies, search, statusFilter, entityFilter]);

  const fiatCurrencies = filteredCurrencies.filter(c => c.type === 'fiat');
  const cryptoCurrencies = filteredCurrencies.filter(c => c.type === 'crypto');

  const renderCurrencyTable = (currencyList: Currency[], type: 'fiat' | 'crypto') => {
    const isCollapsed = collapsedSections.has(type);
    const title = type === 'fiat' ? t('currencies.fiat') : t('currencies.crypto');

    return (
      <div className="card mb-4">
        <div
          className="flex items-center gap-3 py-3 border-b border-border cursor-pointer"
          onClick={() => toggleSection(type)}
        >
          <span className="text-xs text-foreground-muted">{isCollapsed ? '▶' : '▼'}</span>
          <h3 className="text-lg font-semibold m-0">{title}</h3>
          <span className="text-foreground-muted font-normal text-sm">({currencyList.length})</span>
        </div>

        {!isCollapsed && (
          <table className="table">
            <thead>
              <tr>
                <th>{t('currencies.code')}</th>
                <th>{t('currencies.name')}</th>
                <th>{t('currencies.symbol')}</th>
                <th>{t('currencies.decimals')}</th>
                <th>{t('currencies.entities')}</th>
                <th>{tc('status.label')}</th>
                {isAdmin && <th>{tc('actions.label')}</th>}
              </tr>
            </thead>
            <tbody>
              {currencyList.map((currency) => (
                <tr key={currency.id} className={!currency.is_active ? 'opacity-60' : ''}>
                  <td><strong>{currency.code}</strong></td>
                  <td>{currency.name}</td>
                  <td>{currency.symbol || '-'}</td>
                  <td>{currency.decimals}</td>
                  <td>
                    <div className="flex flex-wrap gap-1">
                      {currency.entities.length === 0 ? (
                        <span className="text-muted">-</span>
                      ) : currency.entities.length <= 3 ? (
                        currency.entities.map(e => (
                          <span key={e.id} className="badge text-[11px] px-1.5 py-0.5 bg-indigo-100 text-indigo-800">{e.code}</span>
                        ))
                      ) : (
                        <>
                          {currency.entities.slice(0, 2).map(e => (
                            <span key={e.id} className="badge text-[11px] px-1.5 py-0.5 bg-indigo-100 text-indigo-800">{e.code}</span>
                          ))}
                          <span className="badge text-[11px] px-1.5 py-0.5 bg-gray-200 text-gray-600 cursor-help" title={currency.entities.map(e => e.code).join(', ')}>
                            +{currency.entities.length - 2}
                          </span>
                        </>
                      )}
                    </div>
                  </td>
                  <td>
                    <span
                      className={`badge ${currency.is_active ? 'badge-closed' : 'badge-open'} ${isAdmin ? 'cursor-pointer' : ''}`}
                      onClick={() => isAdmin && handleToggleActive(currency)}
                    >
                      {currency.is_active ? tc('status.active') : tc('status.inactive')}
                    </span>
                  </td>
                  {isAdmin && (
                    <td>
                      <button className="btn btn-secondary px-2 py-1 text-xs" onClick={() => setEditingCurrency(currency)}>
                        {tc('actions.edit')}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
              {currencyList.length === 0 && (
                <tr><td colSpan={isAdmin ? 7 : 6} className="text-center text-muted">{t('currencies.noCurrencies')}</td></tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    );
  };

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">{t('currencies.title')}</h1>
        {isAdmin && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            {t('currencies.addCurrency')}
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <input
          type="text"
          className="form-input !w-auto min-w-[220px]"
          placeholder="Search currencies..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className="form-input !w-auto min-w-[150px]"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">{tc('all')} Status</option>
          <option value="active">{tc('status.active')}</option>
          <option value="inactive">{tc('status.inactive')}</option>
        </select>
        <select
          className="form-input !w-auto min-w-[150px]"
          value={entityFilter}
          onChange={(e) => setEntityFilter(e.target.value)}
        >
          <option value="all">{tc('all')} Entities</option>
          {entities.map(e => (
            <option key={e.id} value={e.id}>{e.code} - {e.name}</option>
          ))}
        </select>
        <span className="ml-auto text-sm text-foreground-muted">{filteredCurrencies.length} currencies</span>
      </div>

      {isLoading ? (
        <p className="text-muted">{tc('actions.loading')}</p>
      ) : (
        <>
          {renderCurrencyTable(fiatCurrencies, 'fiat')}
          {renderCurrencyTable(cryptoCurrencies, 'crypto')}
        </>
      )}

      {showForm && (
        <CreateCurrencyModal
          entities={entities}
          onClose={() => setShowForm(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['currencies-all'] });
            queryClient.invalidateQueries({ queryKey: ['currencies'] });
            setShowForm(false);
            toast.success('Currency created');
          }}
        />
      )}

      {editingCurrency && (
        <EditCurrencyModal
          currency={editingCurrency}
          entities={entities}
          onClose={() => setEditingCurrency(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['currencies-all'] });
            queryClient.invalidateQueries({ queryKey: ['currencies'] });
            setEditingCurrency(null);
            toast.success('Currency updated');
          }}
        />
      )}

    </div>
  );
}

function CreateCurrencyModal({
  entities,
  onClose,
  onSuccess,
}: {
  entities: Entity[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const [selectedEntities, setSelectedEntities] = useState<string[]>(entities.map(e => e.id));

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<CreateCurrencyFormData>({
    resolver: zodResolver(createCurrencySchema),
    defaultValues: {
      type: 'fiat',
      decimals: 2,
    },
  });

  const createMutation = useMutation({
    mutationFn: async (data: CreateCurrencyFormData) => {
      const res = await fetch('/api/admin/currencies', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          code: data.code.toUpperCase(),
          name: data.name,
          symbol: data.symbol || undefined,
          type: data.type,
          decimals: data.decimals,
          entityIds: selectedEntities,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to create currency');
      }
      return res.json();
    },
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const onSubmit = (data: CreateCurrencyFormData) => {
    createMutation.mutate(data);
  };

  const toggleEntity = (entityId: string) => {
    setSelectedEntities(prev =>
      prev.includes(entityId)
        ? prev.filter(id => id !== entityId)
        : [...prev, entityId]
    );
  };

  const toggleAll = () => {
    if (selectedEntities.length === entities.length) {
      setSelectedEntities([]);
    } else {
      setSelectedEntities(entities.map(e => e.id));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('currencies.newCurrency')}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">{t('currencies.code')}</label>
          <input
            type="text"
            className={`form-input uppercase ${errors.code ? 'border-error' : ''}`}
            maxLength={10}
            placeholder="USD, EUR, USDT..."
            {...register('code')}
          />
          {errors.code && (
            <span className="block text-error text-[13px] mt-1">{errors.code.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.name')}</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            placeholder="US Dollar"
            {...register('name')}
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.symbol')}</label>
          <input
            type="text"
            className="form-input"
            maxLength={10}
            placeholder="$, €, ₿..."
            {...register('symbol')}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.type')}</label>
          <select className="form-input" {...register('type')}>
            <option value="fiat">{t('currencies.fiat')}</option>
            <option value="crypto">{t('currencies.crypto')}</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.decimals')}</label>
          <input
            type="number"
            className="form-input"
            min={0}
            max={18}
            {...register('decimals')}
          />
          <small className="text-muted">{t('currencies.decimalsHint')}</small>
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.entities')}</label>
          <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto p-2 border border-border rounded">
            <div className="flex items-center gap-2 pb-2 border-b border-border mb-2">
              <input
                type="checkbox"
                id="select-all-entities"
                className="w-4 h-4"
                checked={selectedEntities.length === entities.length}
                onChange={toggleAll}
              />
              <label htmlFor="select-all-entities" className="text-sm cursor-pointer"><strong>{tc('all')}</strong></label>
            </div>
            {entities.map(entity => (
              <div key={entity.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`entity-${entity.id}`}
                  className="w-4 h-4"
                  checked={selectedEntities.includes(entity.id)}
                  onChange={() => toggleEntity(entity.id)}
                />
                <label htmlFor={`entity-${entity.id}`} className="text-sm cursor-pointer">{entity.code} - {entity.name}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? tc('actions.loading') : tc('actions.create')}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function EditCurrencyModal({
  currency,
  entities,
  onClose,
  onSuccess,
}: {
  currency: Currency;
  entities: Entity[];
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation('admin');
  const { t: tc } = useTranslation('common');
  const toast = useToast();
  const [selectedEntities, setSelectedEntities] = useState<string[]>(
    currency.entities.map(e => e.id)
  );

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<EditCurrencyFormData>({
    resolver: zodResolver(editCurrencySchema),
    defaultValues: {
      name: currency.name,
      symbol: currency.symbol || '',
      type: currency.type,
      decimals: currency.decimals,
      isActive: currency.is_active,
    },
  });

  const updateMutation = useMutation({
    mutationFn: async (data: EditCurrencyFormData) => {
      const res = await fetch(`/api/admin/currencies/${currency.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: data.name,
          symbol: data.symbol || undefined,
          type: data.type,
          decimals: data.decimals,
          isActive: data.isActive,
          entityIds: selectedEntities,
        }),
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.message || 'Failed to update currency');
      }
      return res.json();
    },
    onSuccess,
    onError: (err: Error) => toast.error(err.message),
  });

  const onSubmit = (data: EditCurrencyFormData) => {
    updateMutation.mutate(data);
  };

  const toggleEntity = (entityId: string) => {
    setSelectedEntities(prev =>
      prev.includes(entityId)
        ? prev.filter(id => id !== entityId)
        : [...prev, entityId]
    );
  };

  const toggleAll = () => {
    if (selectedEntities.length === entities.length) {
      setSelectedEntities([]);
    } else {
      setSelectedEntities(entities.map(e => e.id));
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={t('currencies.editCurrency')}
      size="sm"
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="form-group">
          <label className="form-label">{t('currencies.code')}</label>
          <input
            type="text"
            className="form-input"
            value={currency.code}
            disabled
          />
          <small className="text-muted">{t('currencies.codeReadonly')}</small>
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.name')}</label>
          <input
            type="text"
            className={`form-input ${errors.name ? 'border-error' : ''}`}
            placeholder="US Dollar"
            {...register('name')}
          />
          {errors.name && (
            <span className="block text-error text-[13px] mt-1">{errors.name.message}</span>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.symbol')}</label>
          <input
            type="text"
            className="form-input"
            maxLength={10}
            placeholder="$, €, ₿..."
            {...register('symbol')}
          />
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.type')}</label>
          <select className="form-input" {...register('type')}>
            <option value="fiat">{t('currencies.fiat')}</option>
            <option value="crypto">{t('currencies.crypto')}</option>
          </select>
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.decimals')}</label>
          <input
            type="number"
            className="form-input"
            min={0}
            max={18}
            {...register('decimals')}
          />
          <small className="text-muted">{t('currencies.decimalsHint')}</small>
        </div>

        <div className="form-group">
          <label className="form-label">{t('currencies.entities')}</label>
          <div className="flex flex-col gap-2 max-h-[200px] overflow-y-auto p-2 border border-border rounded">
            <div className="flex items-center gap-2 pb-2 border-b border-border mb-2">
              <input
                type="checkbox"
                id="edit-select-all-entities"
                className="w-4 h-4"
                checked={selectedEntities.length === entities.length}
                onChange={toggleAll}
              />
              <label htmlFor="edit-select-all-entities" className="text-sm cursor-pointer"><strong>{tc('all')}</strong></label>
            </div>
            {entities.map(entity => (
              <div key={entity.id} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id={`edit-entity-${entity.id}`}
                  className="w-4 h-4"
                  checked={selectedEntities.includes(entity.id)}
                  onChange={() => toggleEntity(entity.id)}
                />
                <label htmlFor={`edit-entity-${entity.id}`} className="text-sm cursor-pointer">{entity.code} - {entity.name}</label>
              </div>
            ))}
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">{tc('status.label')}</label>
          <select className="form-input" {...register('isActive', { setValueAs: (v) => v === 'true' || v === true })}>
            <option value="true">{tc('status.active')}</option>
            <option value="false">{tc('status.inactive')}</option>
          </select>
        </div>

        <div className="flex gap-2 justify-end">
          <button type="button" className="btn btn-secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={updateMutation.isPending}>
            {updateMutation.isPending ? tc('actions.loading') : tc('actions.save')}
          </button>
        </div>
      </form>
    </Modal>
  );
}
